import { useApiClient } from '@chilipiper/api-client/src/context'
import { useFireSessionContext } from '@chilipiper/fire-session'
import { useWorkspaceId } from '@chilipiper/workspace'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  PublicHandoffDraftWithRouter,
  ExistingRouterCoordinates,
  HandoffRouterDraft,
  Settings,
} from '@chilipiper/api-client/src/handoff'

const queryKey = 'handoff'
export const queryKeyFactory = {
  settings: (workspaceId: string) => [queryKey, 'settings', workspaceId],
  routers: (workspaceId: string) => [queryKey, 'get-routers', workspaceId],
  router: (workspaceId: string, routerId: string) => [
    queryKey,
    'get-router',
    workspaceId,
    routerId,
  ],
  publicRouting: (workspaceId: string, routerId: string) => [
    queryKey,
    'get-public-routing',
    workspaceId,
    routerId,
  ],
}

export const useSettings = () => {
  const { handoff } = useApiClient()
  const { tenantId } = useFireSessionContext()
  const workspaceId = useWorkspaceId()

  return useQuery({
    queryKey: queryKeyFactory.settings(workspaceId),
    queryFn: () => {
      return handoff.getSettings(tenantId, workspaceId)
    },
  })
}

export const useUpdateSettings = () => {
  const queryClient = useQueryClient()
  const { tenantId } = useFireSessionContext()
  const { handoff } = useApiClient()
  const workspaceId = useWorkspaceId()

  return useMutation({
    mutationFn: (payload: Settings) => handoff.updateSettings(tenantId, workspaceId, payload),
    onMutate: (settingsPayload: Settings) => {
      const previousSettings = queryClient.getQueryData(queryKeyFactory.settings(workspaceId))

      queryClient.setQueryData<Settings>(queryKeyFactory.settings(workspaceId), settingsPayload)

      return () => queryClient.setQueryData(queryKeyFactory.settings(workspaceId), previousSettings)
    },
    onSettled: settings => {
      if (settings) {
        void queryClient.invalidateQueries({ queryKey: queryKeyFactory.settings(workspaceId) })
      }
    },
  })
}

export const useRouters = () => {
  const { handoff } = useApiClient()
  const { tenantId } = useFireSessionContext()
  const workspaceId = useWorkspaceId()
  return useQuery({
    queryKey: queryKeyFactory.routers(workspaceId),

    queryFn: () => {
      return handoff.listRouters(tenantId, workspaceId)
    },
  })
}

export const usePublicRoutingInfo = (tenantId: string, routingId?: string) => {
  const { handoff } = useApiClient()
  return useQuery({
    queryKey: queryKeyFactory.publicRouting(tenantId, routingId ?? ''),

    queryFn: () => {
      return handoff.getPublicRoutingInfo(tenantId, routingId ?? '')
    },
    enabled: !!routingId,
  })
}

export const useRouter = (routerId: string) => {
  const { handoff } = useApiClient()
  const { tenantId } = useFireSessionContext()
  const workspaceId = useWorkspaceId()
  return useQuery({
    queryKey: queryKeyFactory.router(workspaceId, routerId),

    queryFn: () => {
      return handoff.getRouter(tenantId, workspaceId, routerId)
    },
  })
}

export const useToggleActivateRouter = (routerId: string) => {
  const queryClient = useQueryClient()
  const { tenantId } = useFireSessionContext()
  const { handoff } = useApiClient()
  const workspaceId = useWorkspaceId()

  return useMutation({
    mutationFn: (acceptsRouting: boolean) =>
      handoff.allowRouting(tenantId, workspaceId, routerId, { acceptsRouting }),
    onMutate: acceptsRouting => {
      const previousItem = queryClient.getQueryData<PublicHandoffDraftWithRouter>(
        queryKeyFactory.router(workspaceId, routerId)
      )
      if (previousItem) {
        queryClient.setQueryData<PublicHandoffDraftWithRouter>(
          queryKeyFactory.router(workspaceId, routerId),
          () => ({ ...previousItem, acceptsRouting, workspaceId })
        )
      }

      return () =>
        queryClient.setQueryData(queryKeyFactory.router(workspaceId, routerId), previousItem)
    },
    onSettled: draftRouter => {
      if (draftRouter) {
        void queryClient.invalidateQueries({
          queryKey: queryKeyFactory.router(workspaceId, draftRouter.id),
        })
      }
    },
  })
}

export const useUpdateRouter = () => {
  const queryClient = useQueryClient()
  const { tenantId } = useFireSessionContext()
  const { handoff } = useApiClient()
  const workspaceId = useWorkspaceId()

  return useMutation({
    mutationFn: (draft: HandoffRouterDraft) =>
      handoff.updateRouter(tenantId, workspaceId, draft).then(() => draft),

    onMutate: draftRouter => {
      const previousItem = queryClient.getQueryData(
        queryKeyFactory.router(workspaceId, draftRouter.id)
      )

      queryClient.setQueryData<PublicHandoffDraftWithRouter>(
        queryKeyFactory.router(workspaceId, draftRouter.id),
        old => ({ ...old, draft: draftRouter, acceptsRouting: true, workspaceId })
      )

      return () =>
        queryClient.setQueryData(queryKeyFactory.router(workspaceId, draftRouter.id), previousItem)
    },

    onSettled: draftRouter => {
      if (draftRouter) {
        void queryClient.invalidateQueries({
          queryKey: queryKeyFactory.router(workspaceId, draftRouter.id),
        })
      }
    },
  })
}

export const usePublishRouter = () => {
  const queryClient = useQueryClient()
  const { tenantId } = useFireSessionContext()
  const { handoff } = useApiClient()
  const workspaceId = useWorkspaceId()

  return useMutation({
    mutationFn: (routerInfo: ExistingRouterCoordinates) => {
      return handoff.publishRouter(tenantId, workspaceId, routerInfo)
    },

    onSuccess: publishedRouter => {
      queryClient.setQueryData<PublicHandoffDraftWithRouter | undefined>(
        queryKeyFactory.router(workspaceId, publishedRouter.id),
        old => (old ? { ...old, router: publishedRouter } : undefined)
      )
    },

    onSettled: publishedRouter => {
      if (publishedRouter) {
        void queryClient.invalidateQueries({
          queryKey: queryKeyFactory.router(workspaceId, publishedRouter.id),
        })
      }
    },
  })
}

export const useCreateRouter = ({
  onSuccess,
  onError,
}: {
  onError?: () => void
  onSuccess?: (id: string) => void
}) => {
  const queryClient = useQueryClient()
  const { handoff } = useApiClient()
  const { tenantId } = useFireSessionContext()
  const workspaceId = useWorkspaceId()

  return useMutation({
    mutationFn: () => handoff.createRouter(tenantId, workspaceId),

    onSuccess: newRouter => {
      queryClient.setQueryData<PublicHandoffDraftWithRouter[]>(
        queryKeyFactory.routers(workspaceId),
        old => {
          const data = old ?? ([] as PublicHandoffDraftWithRouter[])
          data.push({ draft: newRouter, acceptsRouting: true, workspaceId })
          return data
        }
      )

      onSuccess?.(newRouter.id)
    },

    onError,
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.routers(workspaceId) }),
  })
}

export const useDuplicateRouter = ({
  onSuccess,
  onError,
}: {
  onError?: () => void
  onSuccess?: () => void
}) => {
  const queryClient = useQueryClient()
  const { handoff } = useApiClient()
  const { tenantId } = useFireSessionContext()
  const workspaceId = useWorkspaceId()

  return useMutation({
    mutationFn: (routerId: string) => handoff.duplicateRouter(tenantId, workspaceId, routerId),
    onSuccess,
    onError,
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.routers(workspaceId) }),
  })
}

export const useDeleteRouter = ({
  onSuccess,
  onError,
}: {
  onError?: () => void
  onSuccess?: (id: string) => void
}) => {
  const queryClient = useQueryClient()
  const { handoff } = useApiClient()
  const { tenantId } = useFireSessionContext()
  const workspaceId = useWorkspaceId()

  return useMutation({
    mutationFn: (routerId: string) =>
      handoff.deleteRouter(tenantId, workspaceId, routerId).then(() => routerId),

    onMutate: deletedRouterId => {
      const previousItems = queryClient.getQueryData(queryKeyFactory.routers(workspaceId))

      queryClient.setQueryData<PublicHandoffDraftWithRouter[]>(
        queryKeyFactory.routers(workspaceId),
        old => {
          const data = old ?? ([] as PublicHandoffDraftWithRouter[])
          return data.filter(router => router.draft.id !== deletedRouterId)
        }
      )

      return () => queryClient.setQueryData(queryKeyFactory.routers(workspaceId), previousItems)
    },

    onSuccess,
    onError,
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.routers(workspaceId) }),
  })
}
